"use client";

/**
 * Sharing an event, and adding it to the visitor's own calendar. What is shared
 * is the event's address, /evento/<id>/<title>, which the services that show a
 * link's preview read without running the app (the current app's
 * modules/generic/share.js and add_to_calendar.js).
 */
import { eventPath } from "@/lib/address";
import { track } from "@/lib/analytics";
import { isTouchDevice } from "@/lib/device";
import { eventDateWords } from "@/lib/jsonLd";
import { showToast } from "@/lib/toast";

type Shareable = { id: number | string; title?: string | null; start_time?: string | null; end_time?: string | null };

export const eventShareUrl = (event: Shareable) => `${window.location.origin}${eventPath(event)}`;

// titles are typed in by hand, stray spaces and all
const eventTitle = (event: Shareable) => String(event.title || "").trim();

export const eventShareText = (event: Shareable) => `${eventTitle(event)}, ${eventDateWords(event.start_time, event.end_time)}`;

const openInNewTab = (url: string) => window.open(url, "_blank", "noopener");

export type ShareTarget = {
  id: "whatsapp" | "facebook" | "x" | "email" | "copy";
  label: string;
  share: (url: string, text: string) => void;
};

// the ways the menu offers, for a device with no share sheet of its own
export const SHARE_TARGETS: ShareTarget[] = [
  { id: "whatsapp", label: "WhatsApp", share: (url, text) => openInNewTab(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`) },
  { id: "facebook", label: "Facebook", share: (url) => openInNewTab(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`) },
  { id: "x", label: "X", share: (url, text) => openInNewTab(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`) },
  {
    id: "email",
    label: "Email",
    share: (url, text) => {
      window.location.href = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`;
    },
  },
  {
    id: "copy",
    label: "Copiar link",
    share: (url) =>
      navigator.clipboard
        .writeText(url)
        .then(() => showToast("Link copiado"))
        .catch(() => showToast("Não foi possível copiar o link", "error")),
  },
];

export const shareThrough = (event: Shareable, target: ShareTarget) => {
  track("share", { method: target.id, content_type: "evento", item_id: String(event.id) });
  target.share(eventShareUrl(event), eventShareText(event));
};

/**
 * Shares an event through the device's own share sheet, which knows the apps it
 * has, on a phone or tablet that has one. Answers false when there is none, for
 * the caller to open the menu of the usual ways instead.
 */
export const shareNatively = (event: Shareable) => {
  if (!(isTouchDevice() && typeof navigator.share === "function")) {
    return false;
  }
  navigator
    .share({ title: eventTitle(event), text: eventShareText(event), url: eventShareUrl(event) })
    // the sheet does not say which app was picked
    .then(() => track("share", { method: "partilha_do_telemovel", content_type: "evento", item_id: String(event.id) }))
    // closing the sheet without sharing rejects too, and needs nothing
    .catch(() => {});
  return true;
};

/**
 * Adding an event to the visitor's own calendar, both ways served by the public
 * API. On Android, Google Calendar's form for a new event, filled in, in a tab
 * of its own, which the Google Calendar app takes over where it is installed.
 * Everywhere else the event as a calendar file, which Apple Calendar and
 * Outlook open, offering to add it; the page stays put, the file being a download.
 */
export const addEventToCalendar = (event: { id: number | string }) => {
  const base = `/public-api/event/${event.id}/calendar`;
  const android = /Android/i.test(navigator.userAgent);
  track("add_to_calendar", { item_id: String(event.id), calendar: android ? "google" : "ics" });
  if (android) {
    window.open(`${base}/google`, "_blank");
    return;
  }
  // a download, not a page: the browser saves the file and the page stays put
  const link = document.createElement("a");
  link.href = `${base}.ics`;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  link.remove();
};
